import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CubeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  PowerIcon,
} from '@heroicons/react/24/outline';
import {
  useGetAllPlansQuery,
  useGetPlanDetailsQuery,
  useDeletePlanMutation,
  useTogglePlanStatusMutation,
  useDuplicatePlanMutation,
} from '../../store/api/superAdminApi';
import { formatCurrency } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import PlanDetailsModal from './components/PlanDetailsModal';
import CreateEditPlanModal from './components/CreateEditPlanModal';
import toast from 'react-hot-toast';

const PlanManagement = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [country, setCountry] = useState('');
  const [page, setPage] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, error, refetch } = useGetAllPlansQuery({
    page,
    limit: 10,
    search,
    status,
    country
  });

  const { data: planDetailsData, isLoading: isLoadingDetails } = useGetPlanDetailsQuery(
    selectedPlanId,
    { skip: !selectedPlanId }
  );

  const [deletePlan, { isLoading: isDeleting }] = useDeletePlanMutation();
  const [togglePlanStatus, { isLoading: isToggling }] = useTogglePlanStatusMutation();
  const [duplicatePlan, { isLoading: isDuplicating }] = useDuplicatePlanMutation();

  const plans = data?.data?.plans || [];
  const pagination = data?.data?.pagination || {};

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const handleCountryFilter = (e) => {
    setCountry(e.target.value);
    setPage(1);
  };

  const handleViewDetails = (plan) => {
    setSelectedPlanId(plan._id);
    setSelectedPlan(plan);
    setShowDetailsModal(true);
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setShowCreateModal(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  const handleDeleteClick = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePlan(selectedPlan._id).unwrap();
      toast.success('Plan deactivated successfully');
      setShowDeleteModal(false);
      setSelectedPlan(null);
      refetch();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to deactivate plan');
    }
  };

  const handleToggleStatus = async (plan) => {
    try {
      await togglePlanStatus(plan._id).unwrap();
      toast.success(`Plan ${plan.isActive ? 'deactivated' : 'activated'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const handleDuplicate = async (plan) => {
    try {
      await duplicatePlan(plan._id).unwrap();
      toast.success('Plan duplicated successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to duplicate plan');
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load plans" />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Plan Management</h1>
              <p className="text-gray-600 mt-2">Create and manage subscription plans</p>
            </div>
            <motion.button
              onClick={handleCreatePlan}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Plan
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search plans..."
                value={search}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <FunnelIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <select
                value={status}
                onChange={handleStatusFilter}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="relative">
              <select
                value={country}
                onChange={handleCountryFilter}
                className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Countries</option>
                <option value="US">United States</option>
                <option value="IN">India</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="UK">United Kingdom</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {plans.length} of {pagination.totalRecords} plans
              </span>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
                plan.isPopular ? 'border-blue-500' : 'border-gray-200'
              }`}
            >
              {plan.isPopular && (
                <div className="bg-blue-500 text-white text-center py-2 text-sm font-medium rounded-t-xl">
                  Most Popular
                </div>
              )}
              
              <div className="p-6">
                {/* Plan Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <CubeIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500">{plan.code}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleToggleStatus(plan)}
                      disabled={isToggling}
                      className={`p-1 rounded ${
                        plan.isActive
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={plan.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <PowerIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-4">
                  {plan.originalPrice && plan.discountPercentage > 0 && (
                    <div className="flex items-center mb-2">
                      <span className="text-sm text-gray-500 line-through mr-2">
                        {formatCurrency(plan.originalPrice, plan.currency || 'USD')}
                      </span>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {plan.discountPercentage}% OFF
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(plan.price.monthly, plan.currency || 'USD')}
                    </span>
                    <span className="text-gray-500 ml-1">/month</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatCurrency(plan.price.yearly, plan.currency || 'USD')} yearly
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Key Features</h4>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                      {plan.features.maxEmployees} employees
                    </li>
                    <li className="text-sm text-gray-600 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                      {plan.features.maxBranches} branches
                    </li>
                    <li className="text-sm text-gray-600 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                      {plan.features.storage}GB storage
                    </li>
                  </ul>
                </div>

                {/* Stats */}
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {plan.subscriptionCount || 0}
                    </div>
                    <div className="text-xs text-gray-500">Subscriptions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(plan.monthlyRevenue || 0, plan.currency || 'USD')}
                    </div>
                    <div className="text-xs text-gray-500">Revenue</div>
                  </div>
                </div>

                {/* Status */}
                <div className="mb-6">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    plan.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {plan.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {plan.countries && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500">Available in:</div>
                      <div className="text-xs text-gray-700 mt-1">
                        {plan.countries.join(', ')}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(plan)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      title="View Details"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                      title="Edit Plan"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDuplicate(plan)}
                      disabled={isDuplicating}
                      className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                      title="Duplicate Plan"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(plan)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Delete Plan"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {pagination.current} of {pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.total}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && (
        <PlanDetailsModal
          plan={planDetailsData?.data ? planDetailsData.data : selectedPlan}
          isLoading={isLoadingDetails}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedPlan(null);
            setSelectedPlanId(null);
          }}
        />
      )}

      {showCreateModal && (
        <CreateEditPlanModal
          plan={null}
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            refetch();
            setShowCreateModal(false);
            setSelectedPlan(null);
          }}
        />
      )}

      {showEditModal && (
        <CreateEditPlanModal
          plan={selectedPlan}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Deactivate Plan"
        >
          <div className="p-6">
            <p className="text-gray-600 mb-6">
              Are you sure you want to deactivate the <strong>{selectedPlan?.name}</strong> plan?
              This action will prevent new subscriptions but won't affect existing ones.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PlanManagement;